import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp';
import { motion } from 'framer-motion';
import { Crown, Search, Bot, MessageCircle, Settings, Calendar, Clock, FileText, Phone, Shield, Award, Scale, Monitor, User, AlertCircle, RefreshCw } from 'lucide-react';

import { dashboardService } from '../services/dashboardService';
import RealTimeActivities from './RealTimeActivities';

const Dashboard = () => {
  const navigate = useNavigate();
  const { 
    language, 
    subscription, 
    user, 
    isProfileComplete, 
    recentActivity, 
    addActivity,
    planRestrictions,
    loadDashboardData
  } = useApp();

  // Check if profile is complete on first dashboard entry
  useEffect(() => {
    if (user && !isProfileComplete) {
      // Add activity for profile creation requirement
      addActivity({
        type: 'profile_required',
        title: 'Profile completion required',
        description: 'Please complete your profile to access all features',
        icon: 'user',
        priority: 'high'
      });
      
      // Let route protection handle the redirect
      console.log('Profile not complete, route protection will handle redirect');
    }
  }, [user, isProfileComplete]);

  const translations = {
    english: {
      welcomeBack: 'Welcome back',
      toNyaySahaya: 'to NyaySahaya',
      plans: 'Plans',
      subscription: 'Subscription',
      find: 'Find',
      aLawyer: 'a Lawyer',
      aiAssistant: 'AI Legal Assistant',
      aiSubtitle: 'Ask legal questions & get AI-powered insights',
      my: 'My',
      bookings: 'Bookings',
      chats: 'Chats',
      messages: 'Messages',
      totalBookings: 'Total Bookings',
      pendingBookings: 'Pending',
      completedBookings: 'Completed',

      settings: 'Settings',
      consultedWith: 'Consulted with',
      documentUploaded: 'Document uploaded',
      aiChatSession: 'AI chat session started',
      bookingConfirmed: 'Booking confirmed with',
      minutesAgo: 'minutes ago',
      hoursAgo: 'hours ago',
      daysAgo: 'days ago',
      profileRequired: 'Profile completion required',
      completeProfile: 'Complete Profile',
      subscriptionRequired: 'Subscription required',
      upgradeNow: 'Upgrade Now',
      quickActions: 'Quick Actions',
      emergencyLegalHelp: 'Emergency Legal Help',
      hirePersonalCaseManager: 'Hire Personal Case Manager',
      legalForms: 'Legal Forms',
      whyChoose: 'Why Choose NyaySahaya?',
      verifiedLawyers: 'Verified Lawyers',
      verifiedLawyersDesc: 'All lawyers are verified and qualified',
      support24: '24/7 Support',
      support24Desc: 'Get help anytime, anywhere',
      bestPrices: 'Best Prices',
      bestPricesDesc: 'Affordable legal consultation'
    },
    hindi: {
      welcomeBack: 'वापसी पर स्वागत है',
      toNyaySahaya: 'NyaySahaya में',
      plans: 'योजनाएं',
      subscription: 'सदस्यता',
      find: 'खोजें',
      aLawyer: 'एक वकील',
      aiAssistant: 'AI कानूनी सहायक',
      aiSubtitle: 'कानूनी प्रश्न पूछें और AI-संचालित अंतर्दृष्टि प्राप्त करें',
      my: 'मेरी',
      bookings: 'बुकिंग',
      chats: 'चैट',
      messages: 'संदेश',
      totalBookings: 'कुल बुकिंग',
      pendingBookings: 'लंबित',
      completedBookings: 'पूर्ण',

      settings: 'सेटिंग्स',
      consultedWith: 'के साथ परामर्श किया',
      documentUploaded: 'दस्तावेज़ अपलोड किया गया',
      aiChatSession: 'AI चैट सत्र शुरू किया गया',
      bookingConfirmed: 'के साथ बुकिंग पुष्ट',
      minutesAgo: 'मिनट पहले',
      hoursAgo: 'घंटे पहले',
      daysAgo: 'दिन पहले',
      profileRequired: 'प्रोफाइल पूरा करना आवश्यक है',
      completeProfile: 'प्रोफाइल पूरा करें',
      subscriptionRequired: 'सदस्यता आवश्यक है',
      upgradeNow: 'अभी अपग्रेड करें',
      quickActions: 'त्वरित कार्य',
      emergencyLegalHelp: 'आपातकालीन कानूनी सहायता',
      hirePersonalCaseManager: 'व्यक्तिगत केस मैनेजर हायर करें',
      legalForms: 'कानूनी फॉर्म',
      whyChoose: 'NyaySahaya क्यों चुनें?',
      verifiedLawyers: 'सत्यापित वकील',
      verifiedLawyersDesc: 'सभी वकील सत्यापित और योग्य हैं',
      support24: '24/7 सहायता',
      support24Desc: 'कभी भी, कहीं भी सहायता प्राप्त करें',
      bestPrices: 'बेस्ट कीमतें',
      bestPricesDesc: 'किफायती कानूनी परामर्श'
    }
  };

  const t = translations[language] || translations.english;

  const mainTiles = [
    {
      id: 'plans',
      title: t.plans,
      subtitle: t.subscription,
      icon: <div className="flex justify-center items-center p-3 rounded-lg bg-gray-100 dark:bg-gray-700">
        <Crown className="w-6 h-6 text-purple-600 dark:text-purple-400" />
      </div>,
      path: '/plans',
      color: 'bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700'
    },
    {
      id: 'findLawyer',
      title: t.find,
      subtitle: t.aLawyer,
      icon: <div className="flex justify-center items-center p-3 rounded-lg bg-gray-100 dark:bg-gray-700">
        <Search className="w-6 h-6 text-purple-600 dark:text-purple-400" />
      </div>,
      path: '/find-lawyer',
      color: 'bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700',
      disabled: !planRestrictions.canAccessLawyers
    },
    {
      id: 'aiAssistant',
      title: t.aiAssistant,
      subtitle: t.aiSubtitle,
      icon: <div className="flex justify-center items-center p-3 rounded-lg bg-purple-500/20">
        <Bot className="w-6 h-6 text-white" />
      </div>,
      path: '/ai-assistant',
      color: 'bg-gradient-to-br from-purple-600 to-purple-500',
      fullWidth: true,
      disabled: !planRestrictions.canUseAIAssistant
    },
    {
      id: 'myBookings',
      title: t.my,
      subtitle: t.bookings,
      icon: <div className="flex justify-center items-center p-3 rounded-lg bg-gray-100 dark:bg-gray-700">
        <Calendar className="w-6 h-6 text-purple-600 dark:text-purple-400" />
      </div>,
      path: '/my-bookings',
      color: 'bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700'
    },
    {
      id: 'chats',
      title: t.chats,
      subtitle: t.messages,
      icon: <div className="flex justify-center items-center p-3 rounded-lg bg-gray-100 dark:bg-gray-700">
        <MessageCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />
      </div>,
      path: '/chats',
      color: 'bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700'
    }
  ];

  const quickActions = [
    {
      id: 'emergency',
      title: t.emergencyLegalHelp,
      path: '/find-lawyer'
    },
    {
      id: 'personalCaseManager',
      title: t.hirePersonalCaseManager,
      path: '/hire-case-manager'
    },
    {
      id: 'legalForms',
      title: t.legalForms,
      path: '/legal-forms'
    }
  ];

  const handleTileClick = (tile) => {
    if (tile.disabled) {
      if (!planRestrictions.canAccessLawyers) {
        addActivity({
          type: 'subscription_required',
          title: 'Subscription required',
          description: 'Upgrade your plan to access lawyer features',
          icon: 'alert',
          priority: 'medium'
        });
        navigate('/plans');
      }
      return;
    }
    navigate(tile.path);
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  // Refresh dashboard data
  const handleRefreshDashboard = async () => {
    try {
      if (user?.uid) {
        // Generate fresh sample activities
        const sampleActivities = dashboardService.generateSampleActivities(user.uid);
        
        // Add refresh activity
        addActivity({
          type: 'dashboard_refresh',
          title: 'Activities refreshed',
          description: 'Recent activities updated successfully',
          icon: 'refresh',
          priority: 'low'
        });
        
        console.log('✅ Dashboard activities refreshed');
      }
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
    }
  };

  // If profile is not complete, show profile completion banner
  if (!isProfileComplete) {
    return (
      <div className="app-screen-container" style={{ minHeight: '100vh', paddingTop: '0px', paddingBottom: '0px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div className="app-header" style={{ flex: '0 0 auto', padding: '10px' }}>
          <div className="header-content">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Scale className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-text-muted font-medium">{t.welcomeBack}</p>
                  <h1 className="header-title">{t.toNyaySahaya}</h1>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mobile-padding space-y-4" style={{ flexGrow: 1, padding: '10px' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-white/20 rounded-lg">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">{t.profileRequired}</h3>
                <p className="text-blue-100">Complete your profile to access all features</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/profile')}
              className="w-full bg-white text-blue-600 py-3 px-4 rounded-xl font-medium hover:bg-blue-50 transition-colors"
            >
              {t.completeProfile}
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-screen-container" style={{ minHeight: '100vh', paddingTop: '0px', paddingBottom: '0px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      {/* iPhone 14 Pro Header */}
      <div className="app-header" style={{ flex: '0 0 auto', padding: '10px' }}>
        <div className="header-content">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-text-muted font-medium">{t.welcomeBack}</p>
                <h1 className="header-title">{t.toNyaySahaya}</h1>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* Refresh Dashboard Button */}
            <button
              onClick={handleRefreshDashboard}
              className="back-button"
              aria-label="Refresh Dashboard"
            >
              <span className="icon-bg flex items-center justify-center p-0">
                <RefreshCw className="w-6 h-6 text-purple-600" />
              </span>
            </button>
            
            {/* Desktop-only Admin Button */}
            <button
              onClick={() => navigate('/admin-login')}
              className="hidden md:flex items-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
              aria-label="Admin Login"
            >
              <Monitor className="w-4 h-4" />
              <span>Admin</span>
            </button>
            
            <button
              onClick={handleSettingsClick}
              className="back-button"
              aria-label="Settings"
            >
              <span className="icon-bg flex items-center justify-center p-0">
                <Settings className="w-6 h-6 text-purple-600" />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* iPhone 14 Pro Main Content */}
      <div className="container mobile-padding space-y-4" style={{ flexGrow: 1, padding: '10px' }}>


        {/* iPhone 14 Pro Main Tiles Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          {/* Top Row - Plans and Find Lawyer (Always Side by Side) */}
          <div className="grid grid-cols-2 gap-4">
            {mainTiles.slice(0, 2).map((tile, index) => (
              <motion.div
                key={tile.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                onClick={() => handleTileClick(tile)}
                className={`${tile.color} p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-all duration-200 transform hover:scale-105 touch-friendly ${
                  tile.disabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <div className="text-center flex flex-col items-center">
                  {tile.icon}
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mt-3">{tile.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{tile.subtitle}</p>
                  {tile.disabled && (
                    <div className="mt-2 text-xs text-red-600 font-medium">
                      {!planRestrictions.canAccessLawyers ? t.subscriptionRequired : 'Feature disabled'}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* AI Legal Assistant - Full Width */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            onClick={() => handleTileClick(mainTiles[2])}
            className={`bg-gradient-to-br from-purple-600 to-purple-500 rounded-xl p-4 sm:p-6 shadow-sm cursor-pointer hover:shadow-lg transition-all duration-200 transform hover:scale-105 touch-friendly w-full ${
              mainTiles[2].disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <div className="text-center text-gray-100 flex flex-col items-center">
              {mainTiles[2].icon}
              <h3 className="text-lg font-medium text-white mt-3">{mainTiles[2].title}</h3>
              <p className="text-sm text-gray-200">{mainTiles[2].subtitle}</p>
              {mainTiles[2].disabled && (
                <div className="mt-2 text-xs text-red-200 font-medium">
                  {t.subscriptionRequired}
                </div>
              )}
            </div>
          </motion.div>

          {/* Bottom Row - My Bookings and Chats (Always Side by Side) */}
          <div className="grid grid-cols-2 gap-4">
            {mainTiles.slice(3, 5).map((tile, index) => (
              <motion.div
                key={tile.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                onClick={() => handleTileClick(tile)}
                className={`${tile.color} p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-all duration-200 transform hover:scale-105 touch-friendly`}
              >
                <div className="text-center flex flex-col items-center">
                  {tile.icon}
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mt-3">{tile.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{tile.subtitle}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* iPhone 14 Pro Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="space-y-4"
        >
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">{t.quickActions}</h2>
          <div className="grid grid-cols-2 gap-4">
            {quickActions.map((action, index) => (
              <motion.button
                key={action.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                onClick={() => handleTileClick(action)}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-all duration-200 transform hover:scale-105 touch-friendly"
              >
                <div className="flex items-center justify-between h-full">
                  <div className="text-left">
                    <p className="font-medium text-purple-600 dark:text-purple-400 text-sm leading-tight">{action.title}</p>
                  </div>
                  <div className="text-purple-600 dark:text-purple-400">
                    <span className="text-lg font-bold">&gt;</span>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Real-time Recent Activity Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <RealTimeActivities />
        </motion.div>

        {/* Features Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">{t.whyChoose}</h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Shield size={16} className="text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{t.verifiedLawyers}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t.verifiedLawyersDesc}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Clock size={16} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{t.support24}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t.support24Desc}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Award size={16} className="text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{t.bestPrices}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t.bestPricesDesc}</p>
              </div>
            </div>
          </div>
        </motion.div>

      </div>

      {/* Bottom Navigation (if any) */}
      <div className="bottom-navigation" style={{ flex: '0 0 auto', padding: '10px', height: 'clamp(3rem, 5vh, 4rem)' }}>
        {/* Add bottom navigation items here if needed */}
      </div>
    </div>
  );
};

export default Dashboard;